import React, { useState, useMemo } from 'react';

import { List } from 'antd';

import styled from 'styled-components';

import { showDateNoTime } from '@libs/NwMoment';
import { NotAllowedText } from '@libs/NwTexts';

import { useUserPermission } from '@contexts/UserContext';

import AddressLine from '@components/Addresses/AddressLine';
import ContactConnectionDrawer from '@components/Connection/ContactConnectionDrawer';
import { NwEditButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import { PhoneLine, MailLine } from '@components/Gui/NwContactInfo';
import NwDivider from '@components/Gui/NwDivider';
import NwEllipsis from '@components/Gui/NwEllipsis';
import NwIcon from '@components/Gui/NwIcon';
import { NwTag } from '@components/Gui/NwTag';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    height: ${(props) => !props.fluid && '100%'};
    .ant-card {
        height: ${(props) => !props.fluid && '100%'};
    }
    .ant-card-body-content {
        padding: ${(props) => !props.fluid && 0} !important;
    }

    .content-wrapper {
        padding: 1rem;
        .default-connections-bar {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-around;
        }
        .lists-container {
            padding: ${(props) => (props.$fullWidth ? '1rem 2rem' : '0')};
            display: flex;
            flex-direction: ${(props) => (props.$fullWidth ? 'row' : 'column')};

            .lists-container-addresses {
                padding: ${(props) => (props.$fullWidth ? '0 1rem 0 0' : '0')};

                flex: 1 1 0;

                .ant-list {
                    border-right: ${(props) => (props.$fullWidth ? props.theme.DefaultBorder : 'none')};
                }
            }
            .lists-container-staff {
                margin: ${(props) => (props.$fullWidth ? '0 0 0 1rem' : '0')};
                flex: 1 1 0;
            }
        }
    }
`;

const StyledListItem = styled(List.Item)`
    padding: 0.5rem 0;
    .ant-list-item-meta {
        align-items: flex-start;
        .ant-list-item-meta-avatar {
        }
        .ant-list-item-meta-content {
            .ant-list-item-meta-title {
                margin-bottom: 0;
            }
            .ant-list-item-meta-description {
                color: ${(props) => props.theme.TextColor};
                font-size: ${(props) => props.theme.FontSize};

                .addresses-and-phones {
                    display: flex;
                    justify-content: flex-start;
                    align-items: flex-start;
                    padding-right: 2rem;

                    .addresses {
                        padding-top: 0.25rem;
                        padding-right: 2rem;
                        line-height: 1.1em;
                        flex: 1 1 0;
                    }

                    .phones {
                        flex: 1 1 0;
                    }
                }

                .notes-and-phones {
                    display: flex;
                    justify-content: flex-start;
                    align-items: flex-start;

                    .notes {
                        padding-top: 0.25rem;
                        padding-right: 2rem;
                        line-height: 1.1em;
                        flex: 1 1 0;
                    }

                    .phones {
                        flex: 1 1 0;
                    }
                }
            }
        }
    }
`;

const LockContainer = styled.div`
    display: flex;
    align-items: middle;
    opacity: 0.6;
    margin: 2rem auto 1rem auto;
    gap: 1rem;
    .icon-container {
        font-size: 2rem;
    }
    p {
        font-size: 0.9rem;
        max-width: 20rem;
        text-transform: uppercase;
    }
`;

const PhoneWrapper = styled.div`
    cursor: pointer;
`;

const CustomerDetailsConnections = ({
    currentContact,
    currentContactConnections,
    contactID,
    readOnly,
    fluid,
    isInCardTab,
    fullWidth,
}) => {
    // const [selectedEmail, setSelectedEmail] = useState();
    // const [isAddConnectionModal, setAddConnectionModal] = useState(false);
    const [isEditConnection, setIsEditConnection] = useState(false);
    const [connectionId, setConnectionId] = useState();
    // const breakpoints = useNwBreakPoints();

    const viewClientConnections = useUserPermission(
        'ViewClientConnections',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const viewAgencyConnections = useUserPermission(
        'ViewAgencyConnections',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );
    const viewServiceConnections = useUserPermission(
        'ViewServiceConnections',
        currentContact && currentContact.Users.map((user) => user.UserID)
    );

    const viewConnections = useMemo(() => {
        if (currentContact) {
            if (currentContact.SubType === 1) return viewClientConnections;
            if (currentContact.SubType === 2) return viewAgencyConnections;
            return viewServiceConnections;
        }
        return false;

    }, [
        currentContact,
        currentContactConnections,
        viewClientConnections,
        viewAgencyConnections,
        viewServiceConnections,
    ]);

    const { staff, addresses } = useMemo(() => {
        return {
            staff: currentContactConnections
                ? currentContactConnections.filter((connection) => connection.TypeName === 'People')
                : [],
            addresses: currentContactConnections
                ? currentContactConnections.filter((connection) => connection.TypeName === 'Place')
                : [],
        };
    }, [currentContactConnections]);

    // const handleCloseModal = () => {
    //     setSelectedEmail();
    //     setAddConnectionModal(false);
    // };

    // const handleUpdateEmail = email => {
    //     setSelectedEmail(email);
    // };

    const handleOpenConnection = (connection) => () => {
        setConnectionId(connection.ID);
        setIsEditConnection(true);
    };

    const handleEditConnection = () => {
        setIsEditConnection(true);
    };

    const handleCloseEditConnection = () => {
        setIsEditConnection(false);
        if (connectionId) {
            setConnectionId();
        }
    };

    const renderConnections = () => {
        return (
            <>
                {viewConnections ? (
                    <div className="content-wrapper">
                        {currentContact.DefaultConnection && (
                            <div className="default-connections-bar">
                                {currentContact.DefaultConnection.Mobile && (
                                    <PhoneLine
                                        noIcon
                                        mode="button"
                                        phoneNumber={currentContact.DefaultConnection.Mobile}
                                        phoneType="Mobile"
                                    />
                                )}
                                {currentContact.DefaultConnection.Phone && (
                                    <PhoneLine
                                        noIcon
                                        mode="button"
                                        phoneNumber={currentContact.DefaultConnection.Phone}
                                        phoneType="Land"
                                    />
                                )}
                                {currentContact.DefaultConnection.Email && (
                                    <MailLine
                                        noIcon
                                        mode="button"
                                        mailAddress={currentContact.DefaultConnection.Email}
                                        mailCC={currentContact.DefaultConnection.CC}
                                    />
                                )}
                            </div>
                        )}
                        <div className="lists-container">
                            <div className="lists-container-addresses">
                                <NwDivider>Addresses</NwDivider>
                                <List
                                    itemLayout="horizontal"
                                    dataSource={addresses}
                                    renderItem={(connection) => (
                                        <StyledListItem>
                                            <List.Item.Meta
                                                title={
                                                    <div
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={handleOpenConnection(connection)}
                                                    >
                                                        {connection.TypeName === 'Place' ? (
                                                            <strong>{connection.Name}</strong>
                                                        ) : (
                                                            <>
                                                                <strong>{connection.Name}</strong>
                                                                <br />
                                                                {`${connection.SubTypeName}${connection.Role ? ` / ${connection.Role}` : ''}`}
                                                            </>
                                                        )}
                                                    </div>
                                                }
                                                description={
                                                    <div className="addresses-and-phones">
                                                        <div className="addresses">
                                                            {connection.Addresses.map((address) => (
                                                                <AddressLine
                                                                    key={`address-${address.ID}`}
                                                                    address={address}
                                                                    twoLines
                                                                />
                                                            ))}
                                                        </div>
                                                        <div className="phones">
                                                            {connection.Emails.map((email) => (
                                                                <MailLine
                                                                    key={email.ID}
                                                                    mailAddress={email.Address}
                                                                    mailCC={email.CC}
                                                                    noIcon
                                                                />
                                                            ))}
                                                            {connection.Phones.map((phone) => (
                                                                <PhoneLine
                                                                    key={phone.ID}
                                                                    phoneNumber={phone.Number}
                                                                    phoneType={phone.Type}
                                                                    noIcon
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                }
                                            />
                                        </StyledListItem>
                                    )}
                                />
                            </div>
                            <div className="lists-container-staff">
                                <NwDivider>Staff</NwDivider>
                                <List
                                    itemLayout="horizontal"
                                    dataSource={staff}
                                    renderItem={(connection) => (
                                        <StyledListItem>
                                            <List.Item.Meta
                                                title={
                                                    <div
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={handleOpenConnection(connection)}
                                                    >
                                                        <strong>
                                                            {connection.Name}
                                                            {connection.Surname ? ` ${connection.Surname}` : ''}
                                                        </strong>
                                                        <NwTag style={{ marginLeft: '1rem' }}>
                                                            {connection.Role || 'No Role'}
                                                        </NwTag>
                                                    </div>
                                                }
                                                description={
                                                    <div className="notes-and-phones">
                                                        <div className="notes">
                                                            {connection.Birthday && (
                                                                <p>
                                                                    <NwIcon
                                                                        icon={byPrefixAndName.fasl['birthday-cake']}
                                                                    />
                                                                    &nbsp;{showDateNoTime(connection.Birthday)}
                                                                </p>
                                                            )}
                                                            {connection.Note && (
                                                                <NwEllipsis
                                                                    noFontSize
                                                                    text={connection.Note}
                                                                    popover
                                                                    rows={2}
                                                                />
                                                            )}
                                                        </div>
                                                        <div className="phones">
                                                            {connection.Emails.map((email) => (
                                                                // <div key={email.ID}>
                                                                //     <a href={`mailto:${email.Address}`}>
                                                                //         <NwIcon
                                                                //             icon={byPrefixAndName.fasl['envelope']}
                                                                //         />{' '}
                                                                //         {email.Address}
                                                                //     </a>
                                                                // </div>
                                                                <MailLine
                                                                    key={email.ID}
                                                                    mailAddress={email.Address}
                                                                    mailCC={email.CC}
                                                                    noIcon
                                                                />
                                                            ))}
                                                            {connection.Phones.map((phone) => (
                                                                <PhoneLine
                                                                    key={phone.ID}
                                                                    phoneNumber={phone.Number}
                                                                    phoneType={phone.Type}
                                                                    noIcon
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                }
                                            />
                                        </StyledListItem>
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <LockContainer justifyContent="center" alignItems="center">
                        <div className="icon-container">
                            <NwIcon icon={byPrefixAndName.fasl['lock-alt']} />
                        </div>
                        <p>{NotAllowedText}</p>
                    </LockContainer>
                )}
            </>
        );
    };

    return (
        <Wrapper fluid={fluid} $fullWidth={fullWidth}>
            {isInCardTab ? (
                renderConnections()
            ) : (
                <NwCard
                    title="Contacts"
                    icon={byPrefixAndName.fasl['address-card']}
                    padding={fluid && 'reduced'}
                    extra={<NwEditButton disabled={!viewConnections} ghost onClick={handleEditConnection} />}
                >
                    {renderConnections()}
                </NwCard>
            )}
            {/* {isAddConnectionModal &&
                        <AddToMailingListDrawer
                            isAddConnectionModal={isAddConnectionModal}
                            onCloseModal={handleCloseModal}
                            selectedEmail={selectedEmail}
                            fetchData={updateContactConnections}
                            onUpdateEmail={handleUpdateEmail} />} */}
            {isEditConnection && (
                <ContactConnectionDrawer
                    connectionId={connectionId}
                    contactSubType={currentContact.SubType}
                    contactId={contactID}
                    readOnly={readOnly}
                    onClose={handleCloseEditConnection}
                />
            )}
        </Wrapper>
    );
};

export default CustomerDetailsConnections;
